import type { ReactNode } from "react";
import { useId } from "react";
import { cn } from "../utils/cx.js";
import { Field } from "./Field.js";
import { Button } from "./Button.js";

export interface DatePickerPreset {
  label: ReactNode;
  /** ISO date "YYYY-MM-DD". */
  value: string;
}

export interface DatePickerProps {
  /** ISO date string "YYYY-MM-DD". Controlled. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  locale?: string;
  min?: string;
  max?: string;
  firstDay?: 0 | 1;
  /** Quick-select shortcuts shown beside the calendar. */
  presets?: DatePickerPreset[];
  /** Show the "Today" footer button (default true). */
  showToday?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  label?: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  id?: string;
  className?: string;
}

const HEIGHT = { sm: "h-8", md: "h-9", lg: "h-10" } as const;

/**
 * Native date control — the browser picker keeps the ISO "YYYY-MM-DD" value
 * contract unchanged and themes cleanly against our control tokens.
 */
export function DatePicker(props: DatePickerProps) {
  const {
    value,
    defaultValue,
    onChange,
    min,
    max,
    disabled,
    presets,
    size = "md",
    label,
    help,
    error,
    required,
    id,
    className,
  } = props;
  const auto = useId();
  const inputId = id ?? auto;
  const invalid = error != null;

  const control = (
    <div className="flex flex-col gap-2">
      <input
        id={inputId}
        type="date"
        {...(value !== undefined ? { value } : defaultValue !== undefined ? { defaultValue } : {})}
        min={min}
        max={max}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange?.(e.target.value)}
        className={cn(
          "w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
          HEIGHT[size],
          invalid && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
          className,
        )}
      />
      {presets != null && presets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {presets.map((p, i) => (
            <Button key={i} size="sm" variant="subtle" onClick={() => onChange?.(p.value)}>
              {p.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );

  if (label != null || help != null || error != null) {
    return (
      <Field label={label} required={required} help={help} error={error} htmlFor={inputId}>
        {control}
      </Field>
    );
  }
  return control;
}
